import { ArrowUpRight, ChevronRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal, Overline } from "../components/motion";
import { FOCUS_AREAS, ARCHITECTURE, ENGINE_CHAIN, ENGINE_PARTNERS } from "../data";

export default function GreenFinance() {
  return (
    <>
      <PageHeader
        overline="02 — Green Finance"
        lines={["Financing India's", "EV transition."]}
        subtitle="India's electric transition is becoming an asset-financing opportunity. Ashutosh applies capital, assets, risk and relationships to a new and rapidly scaling asset class."
      />

      {/* Focus areas */}
      <section className="py-20 md:py-28 bg-bg" data-testid="green-focus">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
          <Reveal><Overline>Why Green Finance — and why now</Overline></Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            {FOCUS_AREAS.map((f, i) => (
              <Reveal key={f.key} delay={i * 0.05}>
                <div className="group relative bg-bg h-full overflow-hidden" data-testid={`focus-${f.key}`}>
                  <div className="relative h-44 overflow-hidden">
                    <img src={f.image} alt={f.title} className="w-full h-full object-cover object-center scale-105 group-hover:scale-110 transition-transform duration-1000" />
                    {f.tag && <span className="absolute top-4 left-4 overline text-white bg-brand-green px-3 py-1.5">{f.tag}</span>}
                  </div>
                  <div className="p-7">
                    <h3 className="font-heading font-bold text-ink text-lg tracking-tight group-hover:text-brand-green transition-colors duration-500">{f.title}</h3>
                    <p className="mt-2 font-body text-sm text-ink/55 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ₹190 Cr Architecture */}
      <section className="py-20 md:py-28 bg-surface border-t border-ink/10" data-testid="green-architecture">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <Reveal><Overline>The first Green Mobility financing architecture</Overline></Reveal>
              <Reveal delay={0.05}><h2 className="mt-6 font-heading font-extrabold tracking-tighter text-ink text-4xl sm:text-5xl lg:text-6xl leading-[0.98]">₹190 Crore, <span className="font-display italic font-light text-ink/60">structured.</span></h2></Reveal>
            </div>
            <div className="lg:col-span-4"><Reveal delay={0.1}><p className="font-body text-ink/60 leading-relaxed">A diversified starting platform across vehicles, charging infrastructure and charging networks.</p></Reveal></div>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            {ARCHITECTURE.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.08}>
                <article className="group relative bg-bg h-full flex flex-col" data-testid={`architecture-card-${a.id}`}>
                  <div className="relative overflow-hidden h-56 lg:h-60 bg-white">
                    <img src={a.image} alt={a.name} className="img-grayscale w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-1000" />
                    <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/10 to-transparent" />
                    <span className="absolute top-5 left-5 font-display text-6xl text-brand-green leading-none">{a.id}</span>
                    <span className="absolute bottom-5 left-5 overline text-brand-green bg-surface/95 backdrop-blur px-3 py-1.5">{a.category}</span>
                  </div>
                  <div className="p-8 flex flex-col flex-1 border-t border-ink/10">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="font-heading font-bold text-ink text-xl tracking-tight">{a.name}</h3>
                      <ArrowUpRight size={20} className="text-ink/30 group-hover:text-brand-green group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-300 shrink-0" />
                    </div>
                    <div className="mt-4 font-heading font-extrabold text-4xl text-ink tracking-tight group-hover:text-brand-green transition-colors duration-500">{a.amount}</div>
                    <p className="mt-4 font-body text-sm text-ink/55 leading-relaxed">{a.desc}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Strategic engine */}
      <section className="py-20 md:py-28 bg-bg border-t border-ink/10" data-testid="green-engine">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
          <Reveal><Overline>Ashutosh × Cubenz — the strategic engine</Overline></Reveal>
          <Reveal delay={0.05}><h2 className="mt-6 font-heading font-extrabold tracking-tighter text-ink text-3xl md:text-5xl leading-[1] max-w-3xl">Capital connected to vehicles, charging infrastructure and operators.</h2></Reveal>

          <div className="mt-12 grid md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            {ENGINE_PARTNERS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.06}>
                <div className="bg-surface p-8 h-full flex flex-col justify-between min-h-[190px] group hover:border-brand-green border border-transparent transition-colors duration-300">
                  <div className="overline text-brand-green">Partner 0{i + 1}</div>
                  <div className="mt-4">
                    {p.logo ? (
                      <img src={p.logo} alt={p.name} className="h-9 w-auto object-contain mix-blend-multiply" />
                    ) : (
                      <h3 className="font-heading font-bold text-ink text-xl">{p.name}</h3>
                    )}
                    <p className="mt-3 font-body text-sm text-ink/60">{p.role}</p>
                    {p.note && <p className="mt-1 font-body text-xs text-ink/35">{p.note}</p>}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap items-center gap-x-2 gap-y-3">
              {ENGINE_CHAIN.map((c, i) => (
                <span key={c} className="flex items-center gap-2">
                  <span className="font-heading text-sm md:text-base font-bold text-ink border border-ink/15 px-4 py-2 hover:border-brand-green transition-colors duration-300">{c}</span>
                  {i < ENGINE_CHAIN.length - 1 && <ChevronRight size={18} className="text-brand-green" />}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}><p className="mt-8 font-body text-sm text-ink/40 max-w-2xl">The wider CPO layer extends the model from vehicles and infrastructure into charging-network operations.</p></Reveal>
        </div>
      </section>
    </>
  );
}
